import { createSlice } from '@reduxjs/toolkit';
import { datastreamsApi } from '~/api/datastreams-api';
import type { DatastreamResponse } from '~/api/datastreams-api';

// Define a StreamFile type matching your API
export interface StreamFile {
  id: string;
  name: string;
  size: number;
  url: string;
  created_at: string;
}

// Define the initial state

interface StoredText {
  title: string;
  content: string;
  always_include: boolean;
  hide_from_search_results: boolean;
  enabled?: boolean;
}

interface DatastreamState {
  datastream: DatastreamResponse | null;
  storedText: StoredText[];
  files: StreamFile[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

// 2) Provide actual values in the initializer
const initialState: DatastreamState = {
  datastream: null,
  storedText: [],
  files: [],
  status: 'idle',
  error: null,
};

const datastreamSlice = createSlice({
  name: 'datastream',
  initialState,
  reducers: {
    clearFiles(state) {
      state.files = [];
    },
  },
  extraReducers: (builder) => {
    // Use RTK Query actions in the extraReducers
    builder
      .addMatcher(datastreamsApi.endpoints.getDatastreamById.matchPending, (state) => {
        state.status = 'loading';
      })
      .addMatcher(datastreamsApi.endpoints.getDatastreamById.matchFulfilled, (state, action) => {
        state.status = 'succeeded';
        state.datastream = action.payload;
      })
      .addMatcher(datastreamsApi.endpoints.getDatastreamById.matchRejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch datastream';
      })
      .addMatcher(datastreamsApi.endpoints.updateDatastream.matchPending, (state) => {
        state.status = 'loading';
      })
      .addMatcher(datastreamsApi.endpoints.updateDatastream.matchFulfilled, (state, action) => {
        state.status = 'succeeded';
      })
      .addMatcher(datastreamsApi.endpoints.updateDatastream.matchRejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to update datastream';
      })
      .addMatcher(datastreamsApi.endpoints.deleteDatastream.matchPending, (state) => {
        state.status = 'loading';
      })
      .addMatcher(datastreamsApi.endpoints.deleteDatastream.matchFulfilled, (state, action) => {
        state.status = 'succeeded';
        if (state.datastream?.response.id === action.meta.arg.originalArgs) {
          state.datastream = null;
        }
      })
      .addMatcher(datastreamsApi.endpoints.deleteDatastream.matchRejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to delete datastream';
      })
      .addMatcher(datastreamsApi.endpoints.getStoredText.matchFulfilled, (state, action) => {
        state.status = 'succeeded';
        state.storedText = action.payload.response;
      })
      .addMatcher(datastreamsApi.endpoints.getStoredText.matchPending, (state) => {
        state.status = 'loading';
      })
      .addMatcher(datastreamsApi.endpoints.getStoredText.matchRejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch stored text';
      })
      // getStreamFiles
      .addMatcher(datastreamsApi.endpoints.getStreamFiles.matchPending, (state) => {
        state.status = 'loading';
      })
      .addMatcher(datastreamsApi.endpoints.getStreamFiles.matchFulfilled, (state, action) => {
        state.status = 'succeeded';
        state.files = action.payload.response;
      })
      .addMatcher(datastreamsApi.endpoints.getStreamFiles.matchRejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch files';
      })

      .addMatcher(datastreamsApi.endpoints.updateFilesOnDatastream.matchPending, (state) => {
        state.status = 'loading';
      })
      .addMatcher(datastreamsApi.endpoints.updateFilesOnDatastream.matchFulfilled, (state, action) => {
        state.status = 'succeeded';
        if (state.datastreams?.id === action.payload.response.id) {
          state.datastreams = action.payload.response;
        }
      })
      .addMatcher(datastreamsApi.endpoints.updateFilesOnDatastream.matchRejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to update files on datastream';
      })

      .addMatcher(datastreamsApi.endpoints.indexSelectedFiles.matchPending, (state) => {
        state.status = 'loading';
      })
      .addMatcher(datastreamsApi.endpoints.indexSelectedFiles.matchFulfilled, (state, action) => {
        state.status = 'succeeded';
        if (state.datastreams?.id === action.payload.response.id) {
          state.datastreams = action.payload.response;
        }
      })
      .addMatcher(datastreamsApi.endpoints.indexSelectedFiles.matchRejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to update files on datastream';
      })

      .addMatcher(datastreamsApi.endpoints.deleteFiles.matchPending, (state) => {
        state.status = 'loading';
      })
      .addMatcher(datastreamsApi.endpoints.deleteFiles.matchFulfilled, (state, action) => {
        state.status = 'succeeded';
        if (state.datastreams?.id === action.payload.response.id) {
          state.datastreams = action.payload.response;
        }
      })
      .addMatcher(datastreamsApi.endpoints.deleteFiles.matchRejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to update files on datastream';
      });
  },
});

export const { clearFiles } = datastreamSlice.actions;
export default datastreamSlice.reducer;
